import React, { useState, useEffect } from 'react';
import { Line, Doughnut, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  LineElement, PointElement, LinearScale, CategoryScale,
  Title, Tooltip, Legend, ArcElement, BarElement, Filler
} from 'chart.js';
import * as Authservices from '../Services/services';

ChartJS.register(LineElement, PointElement, LinearScale, CategoryScale, Title, Tooltip, Legend, ArcElement, BarElement, Filler);

const tooltipStyle = { backgroundColor: 'rgba(18,18,30,0.95)', borderColor: 'rgba(255,255,255,0.1)', borderWidth: 1, padding: 12, cornerRadius: 8, titleColor: '#fff', bodyColor: 'rgba(255,255,255,0.8)' };
const gridStyle = { color: 'rgba(255,255,255,0.04)' };
const tickStyle = { color: 'rgba(255,255,255,0.4)', font: { size: 11 } };
const noGrid = { display: false };
const noBorder = { display: false };

const RealTimeAnalytics = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [timeRange, setTimeRange] = useState('7d');
  const [apiData, setApiData] = useState(null);
  const [liveData, setLiveData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [liveOnline, setLiveOnline] = useState(1283);

  useEffect(() => {
    setLoading(true);
    Authservices.getRealtimeAnalytics()
      .then(res => {
        if (res?.status === 200) {
          const details = res.data?.responseDetails || null;
          setApiData(details);
          setLiveData(details);
          if (details?.active_users_now) setLiveOnline(details.active_users_now);
          console.log('[RealTimeAnalytics] API data loaded:', details);
        }
      })
      .catch(err => {
        console.warn('[RealTimeAnalytics] API error, using defaults:', err?.response?.data?.responseMessage || err.message);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      setLiveOnline(prev => Math.max(1, prev + Math.floor(Math.random() * 10 - 4)));
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  const tabs = [
    { id: 'overview', label: 'Overview', icon: 'fas fa-chart-pie' },
    { id: 'behavior', label: 'User Behavior', icon: 'fas fa-mouse-pointer' },
    { id: 'subscriptions', label: 'Subscriptions', icon: 'fas fa-crown' },
    { id: 'technical', label: 'Technical', icon: 'fas fa-microchip' },
    { id: 'engagement', label: 'Engagement', icon: 'fas fa-heart' },
  ];

  const liveStats = liveData ? [
    { label: 'Active Now',          value: liveData.active_users_now,      icon: 'fas fa-circle',       color: '#10b981' },
    { label: 'Streaming Now',       value: liveData.episodes_streaming_now, icon: 'fas fa-play',         color: '#f82581' },
    { label: 'Signups (1h)',        value: liveData.new_signups_last_hour,  icon: 'fas fa-user-plus',    color: '#6366f1' },
    { label: 'Purchases (1h)',      value: liveData.purchases_last_hour,    icon: 'fas fa-shopping-cart',color: '#f59e0b' },
    { label: 'Total VIP Active',    value: liveData.vip_active_total,       icon: 'fas fa-crown',        color: '#ec4899' },
  ] : null;

  return (
    <div className="rta-container">
      <div className="rta-header">
        <div className="rta-header-left">
          <h4>Real-Time Analytics {loading && <span style={{ fontSize: '12px', color: '#8a8a9a', marginLeft: 8 }}>Loading…</span>}</h4>
          <p className="rta-subtitle">Live platform metrics and user intelligence</p>
        </div>
        <div className="rta-header-right">
          <div className="rta-live-badge">
            <span className="rta-live-dot"></span> Live
            <span className="rta-online-count">{liveOnline.toLocaleString()} online</span>
          </div>
        </div>
      </div>

      {liveStats && (
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 24 }}>
          {liveStats.map((s, i) => (
            <div key={i} style={{ flex: '1 1 140px', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 10, padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={{ fontSize: 11, color: '#8a8a9a', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                <i className={s.icon} style={{ color: s.color, marginRight: 6 }}></i>{s.label}
              </span>
              <strong style={{ fontSize: 22, color: '#fff' }}>{Number(s.value).toLocaleString()}</strong>
            </div>
          ))}
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
        <div className="rta-tabs" style={{ margin: 0 }}>
          {tabs.map(t => (
            <button key={t.id} className={`rta-tab ${activeTab === t.id ? 'active' : ''}`} onClick={() => setActiveTab(t.id)}>
              <i className={t.icon}></i> {t.label}
            </button>
          ))}
        </div>
        <div className="rta-time-range">
          {['7d', '30d', '90d'].map(r => (
            <button key={r} className={`rta-range-btn ${timeRange === r ? 'active' : ''}`} onClick={() => setTimeRange(r)}>{r}</button>
          ))}
        </div>
      </div>

      {activeTab === 'overview' && <OverviewTab liveDAU={liveData?.active_users_now || 0} liveOnline={liveOnline} timeRange={timeRange} data={apiData?.overview} />}
      {activeTab === 'behavior' && <BehaviorTab timeRange={timeRange} data={apiData?.behavior} />}
      {activeTab === 'subscriptions' && <SubscriptionsTab timeRange={timeRange} data={apiData?.subscriptions} />}
      {activeTab === 'technical' && <TechnicalTab data={apiData?.technical} />}
      {activeTab === 'engagement' && <EngagementTab />}
    </div>
  );
};

const OverviewTab = ({ liveDAU, liveOnline, timeRange, data }) => {
  const trend = data?.dau_wau_trend;
  const dauMauData = {
    labels: trend?.labels || ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
    datasets: [
      { label: 'DAU', data: trend?.dau || [12200, 13100, 12800, 14500, 13900, 16200, 15800], borderColor: '#f82581', backgroundColor: 'rgba(248,37,129,0.08)', tension: 0.4, fill: true, pointRadius: 0, pointHoverRadius: 5, borderWidth: 2.5 },
      { label: 'WAU', data: trend?.wau || [45000, 46200, 47100, 48500, 49200, 51000, 50300], borderColor: '#6366f1', backgroundColor: 'rgba(99,102,241,0.05)', tension: 0.4, fill: true, pointRadius: 0, pointHoverRadius: 5, borderWidth: 2.5 },
    ],
  };

  const retention = data?.retention_curve || [
    { day: 'Day 1', pct: 100 }, { day: 'Day 3', pct: 68 }, { day: 'Day 7', pct: 45 },
    { day: 'Day 14', pct: 32 }, { day: 'Day 30', pct: 22 }, { day: 'Day 60', pct: 15 }, { day: 'Day 90', pct: 11 },
  ];
  const retentionData = {
    labels: retention.map(r => r.day),
    datasets: [{ label: 'Retention %', data: retention.map(r => r.pct), borderColor: '#10b981', backgroundColor: 'rgba(16,185,129,0.1)', tension: 0.3, fill: true, pointRadius: 4, pointBackgroundColor: '#10b981', pointBorderColor: '#fff', pointBorderWidth: 2, borderWidth: 2.5 }],
  };

  const hourlyRaw = data?.hourly_active_users || [320, 210, 145, 98, 85, 120, 280, 520, 780, 920, 1050, 1180, 1250, 1120, 980, 1050, 1280, 1450, 1380, 1200, 980, 750, 580, 420];
  const hourlyActive = {
    labels: Array.from({ length: 24 }, (_, i) => `${i}:00`),
    datasets: [{ label: 'Active Users', data: hourlyRaw, backgroundColor: Array.from({ length: 24 }, (_, i) => i >= 8 && i <= 20 ? 'rgba(248,37,129,0.6)' : 'rgba(248,37,129,0.25)'), borderRadius: 4, borderSkipped: false, barPercentage: 0.7 }],
  };

  const geoData = data?.geo_distribution || [
    { country: 'United States', users: '3.2M', pct: 28, flag: '🇺🇸' },
    { country: 'India', users: '2.1M', pct: 18, flag: '🇮🇳' },
    { country: 'Philippines', users: '1.8M', pct: 16, flag: '🇵🇭' },
    { country: 'Indonesia', users: '1.4M', pct: 12, flag: '🇮🇩' },
    { country: 'Brazil', users: '980K', pct: 9, flag: '🇧🇷' },
    { country: 'Thailand', users: '720K', pct: 6, flag: '🇹🇭' },
    { country: 'Others', users: '1.3M', pct: 11, flag: '🌍' },
  ];

  const chartOpts = (yCallback) => ({
    responsive: true, maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    plugins: { legend: { display: false }, tooltip: tooltipStyle },
    scales: { x: { ticks: tickStyle, grid: noGrid, border: noBorder }, y: { ticks: { ...tickStyle, callback: yCallback }, grid: gridStyle, border: noBorder } },
  });

  const m = data?.metrics;
  const metrics = [
    { label: 'DAU', value: liveDAU.toLocaleString(), change: m?.dau_change || '+12.3%', positive: true, icon: 'fas fa-users', color: '#f82581' },
    { label: 'MAU', value: m ? Number(m.mau).toLocaleString() : '384,200', change: m?.mau_change || '+8.7%', positive: true, icon: 'fas fa-calendar', color: '#6366f1' },
    { label: 'DAU/MAU Ratio', value: m ? `${m.dau_mau_ratio_pct}%` : '33.4%', change: '+2.1%', positive: true, icon: 'fas fa-percentage', color: '#10b981', desc: 'Stickiness' },
    { label: 'Avg Session', value: m ? `${m.avg_session_min} min` : '18.5 min', change: m?.avg_session_change || '+1.2 min', positive: true, icon: 'fas fa-clock', color: '#f59e0b' },
    { label: 'Sessions/User', value: m ? String(m.sessions_per_user) : '3.2', change: '+0.3', positive: true, icon: 'fas fa-redo', color: '#8b5cf6' },
    { label: 'New Users Today', value: m ? Number(m.new_users_today).toLocaleString() : '1,847', change: m?.new_users_change || '+15.4%', positive: true, icon: 'fas fa-user-plus', color: '#ef4444' },
  ];

  return (
    <div className="rta-section">
      <div className="rta-metrics-grid">
        {metrics.map((m, i) => (
          <div key={i} className="rta-metric-card" style={{ animationDelay: `${i * 0.06}s` }}>
            <div className="rta-metric-top">
              <div className="rta-metric-icon" style={{ background: m.color + '15', color: m.color }}><i className={m.icon}></i></div>
              <span className={`rta-metric-change ${m.positive ? 'up' : 'down'}`}><i className={`fas fa-arrow-${m.positive ? 'up' : 'down'}`}></i> {m.change}</span>
            </div>
            <div className="rta-metric-value">{m.value}</div>
            <div className="rta-metric-label">{m.label}{m.desc && <span className="rta-metric-desc"> · {m.desc}</span>}</div>
          </div>
        ))}
      </div>

      <div className="rta-row-2col">
        <div className="rta-chart-card">
          <div className="rta-chart-head">
            <h6>DAU & WAU Trend</h6>
            <div className="rta-chart-legend-inline">
              <span><span className="rta-dot" style={{ background: '#f82581' }}></span> DAU</span>
              <span><span className="rta-dot" style={{ background: '#6366f1' }}></span> WAU</span>
            </div>
          </div>
          <div className="rta-chart-body" style={{ height: 260 }}><Line data={dauMauData} options={chartOpts(v => (v / 1000).toFixed(0) + 'K')} /></div>
        </div>
        <div className="rta-chart-card">
          <div className="rta-chart-head"><h6>User Retention Curve</h6><span className="rta-badge-sm">Cohort Analysis</span></div>
          <div className="rta-chart-body" style={{ height: 260 }}><Line data={retentionData} options={chartOpts(v => v + '%')} /></div>
        </div>
      </div>

      <div className="rta-row-2col">
        <div className="rta-chart-card">
          <div className="rta-chart-head"><h6>Hourly Active Users</h6><span className="rta-badge-sm">Today</span></div>
          <div className="rta-chart-body" style={{ height: 200 }}>
            <Bar data={hourlyActive} options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false }, tooltip: tooltipStyle }, scales: { x: { ticks: { ...tickStyle, maxRotation: 0, autoSkip: true, maxTicksLimit: 12 }, grid: noGrid, border: noBorder }, y: { ticks: tickStyle, grid: gridStyle, border: noBorder } } }} />
          </div>
          <div className="rta-chart-footer-note"><i className="fas fa-info-circle"></i> Peak hours: 11:00 - 19:00 · Highlighted bars indicate peak activity</div>
        </div>
        <div className="rta-chart-card">
          <div className="rta-chart-head"><h6>Geographic Distribution</h6></div>
          <div className="rta-geo-list">
            {geoData.map((g, i) => (
              <div key={i} className="rta-geo-item">
                <span className="rta-geo-flag">{g.flag}</span>
                <span className="rta-geo-name">{g.country}</span>
                <div className="rta-geo-bar-wrap"><div className="rta-geo-bar" style={{ width: `${g.pct * 3}%` }}></div></div>
                <span className="rta-geo-users">{g.users}</span>
                <span className="rta-geo-pct">{g.pct}%</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const BehaviorTab = ({ timeRange, data }) => {
  const watchFunnel = data?.discovery_funnel || [
    { stage: 'Browse', value: 100, users: '384,200', color: '#6366f1' },
    { stage: 'Preview', value: 72, users: '276,624', color: '#8b5cf6' },
    { stage: 'Start Watching', value: 58, users: '222,836', color: '#f82581' },
    { stage: 'Watch 50%+', value: 38, users: '145,996', color: '#f59e0b' },
    { stage: 'Complete Episode', value: 24, users: '92,208', color: '#10b981' },
    { stage: 'Watch Next', value: 18, users: '69,156', color: '#ef4444' },
  ].map(s => ({ ...s, value: s.pct ?? s.value, color: s.color || '#6366f1' }));

  const searchTerms = data?.trending_searches || [
    { term: 'CEO romance', searches: 12400, trend: 'up' }, { term: 'revenge drama', searches: 9800, trend: 'up' },
    { term: 'love triangle', searches: 8200, trend: 'stable' }, { term: 'billionaire', searches: 7500, trend: 'up' },
    { term: 'secret identity', searches: 6100, trend: 'down' }, { term: 'office romance', searches: 5800, trend: 'up' },
    { term: 'werewolf', searches: 4200, trend: 'stable' }, { term: 'time travel', searches: 3900, trend: 'up' },
  ];

  const dropOffPoints = data?.drop_off || [
    { episode: 'Ep 1 (0-2 min)', dropoff: 8, reason: 'Slow intro' },
    { episode: 'Ep 1 (2-5 min)', dropoff: 15, reason: 'Before hook' },
    { episode: 'Ep 3 end', dropoff: 22, reason: 'Free preview limit' },
    { episode: 'Ep 5-8', dropoff: 12, reason: 'Plot slowdown' },
    { episode: 'Ep 10+', dropoff: 5, reason: 'Invested users stay' },
  ].map(d => ({ ...d, dropoff: d.dropoff_pct ?? d.dropoff }));

  const sessionDist = data?.session_duration || [
    { bucket: '0-2m', pct: 8 }, { bucket: '2-5m', pct: 15 }, { bucket: '5-10m', pct: 22 },
    { bucket: '10-20m', pct: 28 }, { bucket: '20-30m', pct: 18 }, { bucket: '30-60m', pct: 7 }, { bucket: '60m+', pct: 2 },
  ];
  const sessionData = {
    labels: sessionDist.map(s => s.bucket),
    datasets: [{ label: 'Sessions', data: sessionDist.map(s => s.pct), backgroundColor: ['rgba(99,102,241,0.4)', 'rgba(99,102,241,0.5)', 'rgba(248,37,129,0.5)', 'rgba(248,37,129,0.7)', 'rgba(248,37,129,0.5)', 'rgba(16,185,129,0.5)', 'rgba(16,185,129,0.4)'], borderRadius: 6, borderSkipped: false, barPercentage: 0.7 }],
  };

  const maxSearches = searchTerms[0]?.searches || 1;

  return (
    <div className="rta-section">
      <div className="rta-row-2col">
        <div className="rta-chart-card">
          <div className="rta-chart-head"><h6><i className="fas fa-filter"></i> Content Discovery Funnel</h6></div>
          <div className="rta-funnel">
            {watchFunnel.map((s, i) => (
              <div key={i} className="rta-funnel-step" style={{ animationDelay: `${i * 0.1}s` }}>
                <div className="rta-funnel-bar-wrap"><div className="rta-funnel-bar" style={{ width: `${s.value}%`, background: s.color }}></div></div>
                <div className="rta-funnel-info">
                  <span className="rta-funnel-stage">{s.stage}</span>
                  <span className="rta-funnel-users">{s.users}</span>
                  <span className="rta-funnel-pct">{s.value}%</span>
                </div>
                {i < watchFunnel.length - 1 && <div className="rta-funnel-drop"><i className="fas fa-arrow-down"></i> {s.value - watchFunnel[i + 1].value}% drop</div>}
              </div>
            ))}
          </div>
        </div>
        <div className="rta-chart-card">
          <div className="rta-chart-head"><h6><i className="fas fa-clock"></i> Session Duration Distribution</h6><span className="rta-badge-sm">Avg: 18.5 min</span></div>
          <div className="rta-chart-body" style={{ height: 240 }}>
            <Bar data={sessionData} options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false }, tooltip: { ...tooltipStyle, callbacks: { label: ctx => `${ctx.parsed.y}% of sessions` } } }, scales: { x: { ticks: tickStyle, grid: noGrid, border: noBorder }, y: { ticks: { ...tickStyle, callback: v => v + '%' }, grid: gridStyle, border: noBorder } } }} />
          </div>
        </div>
      </div>

      <div className="rta-row-2col">
        <div className="rta-chart-card">
          <div className="rta-chart-head"><h6><i className="fas fa-search"></i> Trending Search Terms</h6></div>
          <div className="rta-search-list">
            {searchTerms.map((s, i) => (
              <div key={i} className="rta-search-item">
                <span className="rta-search-rank">#{i + 1}</span>
                <span className="rta-search-term">{s.term}</span>
                <div className="rta-search-bar-wrap"><div className="rta-search-bar" style={{ width: `${(s.searches / maxSearches) * 100}%` }}></div></div>
                <span className="rta-search-count">{s.searches.toLocaleString()}</span>
                <span className={`rta-search-trend ${s.trend}`}><i className={`fas fa-arrow-${s.trend === 'up' ? 'up' : s.trend === 'down' ? 'down' : 'right'}`}></i></span>
              </div>
            ))}
          </div>
        </div>
        <div className="rta-chart-card">
          <div className="rta-chart-head"><h6><i className="fas fa-sign-out-alt"></i> Drop-off Analysis</h6></div>
          <div className="rta-dropoff-list">
            {dropOffPoints.map((d, i) => (
              <div key={i} className="rta-dropoff-item">
                <div className="rta-dropoff-header">
                  <span className="rta-dropoff-ep">{d.episode}</span>
                  <span className="rta-dropoff-pct" style={{ color: d.dropoff > 15 ? '#ef4444' : d.dropoff > 10 ? '#f59e0b' : '#10b981' }}>-{d.dropoff}%</span>
                </div>
                <div className="rta-dropoff-bar-wrap"><div className="rta-dropoff-bar" style={{ width: `${d.dropoff * 4}%`, background: d.dropoff > 15 ? '#ef4444' : d.dropoff > 10 ? '#f59e0b' : '#10b981' }}></div></div>
                <span className="rta-dropoff-reason"><i className="fas fa-lightbulb"></i> {d.reason}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const SubscriptionsTab = ({ timeRange, data }) => {
  const m = data?.metrics;
  const subMetrics = [
    { label: 'Active VIP', value: m ? Number(m.active_vip).toLocaleString() : '19,210', change: m?.active_vip_change || '+340', positive: true, icon: 'fas fa-crown', color: '#f59e0b' },
    { label: 'Monthly Churn', value: m ? `${m.monthly_churn_pct}%` : '3.5%', change: m?.monthly_churn_change || '-0.3%', positive: true, icon: 'fas fa-user-minus', color: '#ef4444' },
    { label: 'ARPU', value: m ? `$${m.arpu}` : '$3.60', change: m?.arpu_change || '+$0.25', positive: true, icon: 'fas fa-dollar-sign', color: '#10b981' },
    { label: 'LTV', value: m ? `$${m.ltv}` : '$42.80', change: m?.ltv_change || '+$3.20', positive: true, icon: 'fas fa-gem', color: '#8b5cf6' },
  ];

  const funnelSteps = data?.conversion_funnel || [
    { label: 'Free Users', value: 384200, pct: 100, color: '#6366f1' },
    { label: 'Viewed VIP Page', value: 96050, pct: 25, color: '#8b5cf6' },
    { label: 'Started Trial', value: 38420, pct: 10, color: '#f82581' },
    { label: 'Trial Active', value: 26894, pct: 7, color: '#f59e0b' },
    { label: 'Converted to Paid', value: 19210, pct: 5, color: '#10b981' },
  ];

  const ct = data?.churn_trend;
  const churnData = {
    labels: ct?.labels || ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'],
    datasets: [
      { label: 'Churn Rate', data: ct?.churn_rate || [5.2, 4.8, 4.5, 4.1, 3.8, 3.5], borderColor: '#ef4444', backgroundColor: 'rgba(239,68,68,0.08)', tension: 0.4, fill: true, pointRadius: 4, pointBackgroundColor: '#ef4444', pointBorderColor: '#fff', pointBorderWidth: 2, borderWidth: 2.5 },
      { label: 'Recovery Rate', data: ct?.recovery_rate || [1.2, 1.5, 1.8, 2.1, 2.4, 2.8], borderColor: '#10b981', backgroundColor: 'rgba(16,185,129,0.05)', tension: 0.4, fill: true, pointRadius: 4, pointBackgroundColor: '#10b981', pointBorderColor: '#fff', pointBorderWidth: 2, borderWidth: 2.5 },
    ],
  };

  const at = data?.arpu_trend;
  const arpuData = {
    labels: at?.labels || ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'],
    datasets: [{ label: 'ARPU', data: at?.arpu || [2.40, 2.65, 2.80, 3.10, 3.35, 3.60], borderColor: '#f82581', backgroundColor: 'rgba(248,37,129,0.1)', tension: 0.4, fill: true, pointRadius: 4, pointBackgroundColor: '#f82581', pointBorderColor: '#fff', pointBorderWidth: 2, borderWidth: 2.5 }],
  };

  const pd = data?.plan_distribution;
  const planBreakdown = {
    labels: ['Weekly', 'Monthly', 'Quarterly', 'Annual'],
    datasets: [{ data: pd ? [pd.weekly, pd.monthly, pd.quarterly, pd.annual] : [15, 45, 25, 15], backgroundColor: ['#f82581', '#6366f1', '#10b981', '#f59e0b'], borderWidth: 0, cutout: '68%', spacing: 3 }],
  };
  const planValues = pd ? [pd.weekly, pd.monthly, pd.quarterly, pd.annual] : [15, 45, 25, 15];

  const chartOpts = (cb) => ({ responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false }, tooltip: tooltipStyle }, scales: { x: { ticks: tickStyle, grid: noGrid, border: noBorder }, y: { ticks: { ...tickStyle, callback: cb }, grid: gridStyle, border: noBorder } } });

  return (
    <div className="rta-section">
      <div className="rta-metrics-grid rta-metrics-4col">
        {subMetrics.map((m, i) => (
          <div key={i} className="rta-metric-card" style={{ animationDelay: `${i * 0.06}s` }}>
            <div className="rta-metric-top">
              <div className="rta-metric-icon" style={{ background: m.color + '15', color: m.color }}><i className={m.icon}></i></div>
              <span className={`rta-metric-change ${m.positive ? 'up' : 'down'}`}><i className={`fas fa-arrow-${m.positive ? 'up' : 'down'}`}></i> {m.change}</span>
            </div>
            <div className="rta-metric-value">{m.value}</div>
            <div className="rta-metric-label">{m.label}</div>
          </div>
        ))}
      </div>

      <div className="rta-chart-card">
        <div className="rta-chart-head"><h6><i className="fas fa-funnel-dollar"></i> VIP Conversion Funnel</h6></div>
        <div className="rta-conversion-funnel">
          {funnelSteps.map((s, i) => (
            <div key={i} className="rta-conv-step">
              <div className="rta-conv-bar-outer"><div className="rta-conv-bar-inner" style={{ width: `${s.pct}%`, background: s.color || '#6366f1', animationDelay: `${i * 0.15}s` }}></div></div>
              <div className="rta-conv-label"><strong>{s.label}</strong><span>{s.value.toLocaleString()} users ({s.pct}%)</span></div>
              {i < funnelSteps.length - 1 && <div className="rta-conv-arrow"><i className="fas fa-chevron-down"></i> {((funnelSteps[i + 1].value / s.value) * 100).toFixed(0)}% proceed</div>}
            </div>
          ))}
        </div>
      </div>

      <div className="rta-row-3col">
        <div className="rta-chart-card">
          <div className="rta-chart-head">
            <h6>Churn vs Recovery</h6>
            <div className="rta-chart-legend-inline">
              <span><span className="rta-dot" style={{ background: '#ef4444' }}></span> Churn</span>
              <span><span className="rta-dot" style={{ background: '#10b981' }}></span> Recovery</span>
            </div>
          </div>
          <div className="rta-chart-body" style={{ height: 220 }}><Line data={churnData} options={chartOpts(v => v + '%')} /></div>
        </div>
        <div className="rta-chart-card">
          <div className="rta-chart-head"><h6>ARPU Trend</h6></div>
          <div className="rta-chart-body" style={{ height: 220 }}><Line data={arpuData} options={chartOpts(v => '$' + v.toFixed(2))} /></div>
        </div>
        <div className="rta-chart-card">
          <div className="rta-chart-head"><h6>Plan Distribution</h6></div>
          <div className="rta-chart-body rta-doughnut-wrap" style={{ height: 180 }}>
            <Doughnut data={planBreakdown} options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false }, tooltip: { ...tooltipStyle, callbacks: { label: ctx => `${ctx.label}: ${ctx.parsed}%` } } } }} />
          </div>
          <div className="rta-plan-labels">
            {['Weekly', 'Monthly', 'Quarterly', 'Annual'].map((p, i) => (
              <span key={i}><span className="rta-dot" style={{ background: ['#f82581', '#6366f1', '#10b981', '#f59e0b'][i] }}></span> {p} {planValues[i]}%</span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const TechnicalTab = ({ data }) => {
  const crashReports = data?.crash_reports || [
    { id: 'CR-1247', title: 'Video playback crash on low memory devices', platform: 'Android', occurrences: 342, affected_pct: '0.8%', severity: 'high', status: 'investigating' },
    { id: 'CR-1245', title: 'App freeze during coin purchase flow', platform: 'iOS', occurrences: 128, affected_pct: '0.3%', severity: 'critical', status: 'fixing' },
    { id: 'CR-1243', title: 'WebView rendering issue on older browsers', platform: 'Web', occurrences: 89, affected_pct: '0.2%', severity: 'medium', status: 'monitoring' },
    { id: 'CR-1240', title: 'Background download service timeout', platform: 'Android', occurrences: 67, affected_pct: '0.15%', severity: 'low', status: 'resolved' },
    { id: 'CR-1238', title: 'Push notification handler exception', platform: 'iOS', occurrences: 45, affected_pct: '0.1%', severity: 'medium', status: 'resolved' },
  ];

  const apiMetrics = data?.api_metrics || [
    { endpoint: '/api/episodes/stream', p50_ms: 45, p95_ms: 120, p99_ms: 280, error_rate_pct: '0.02%', rps: 2400, status: 'healthy' },
    { endpoint: '/api/auth/token', p50_ms: 12, p95_ms: 35, p99_ms: 80, error_rate_pct: '0.01%', rps: 1800, status: 'healthy' },
    { endpoint: '/api/coins/purchase', p50_ms: 180, p95_ms: 450, p99_ms: 920, error_rate_pct: '0.08%', rps: 340, status: 'slow' },
    { endpoint: '/api/series/list', p50_ms: 28, p95_ms: 75, p99_ms: 140, error_rate_pct: '0.00%', rps: 5200, status: 'healthy' },
  ];

  const severityColor = { critical: '#ef4444', high: '#f59e0b', medium: '#6366f1', low: '#10b981' };
  const statusColor = { investigating: '#f59e0b', fixing: '#ef4444', monitoring: '#6366f1', resolved: '#10b981' };
  const apiStatusColor = { healthy: '#10b981', slow: '#f59e0b', degraded: '#ef4444' };

  return (
    <div className="rta-section">
      <div className="rta-chart-card">
        <div className="rta-chart-head"><h6><i className="fas fa-bug"></i> Crash Reports</h6></div>
        <div className="pa-table-wrap">
          <table className="pa-table">
            <thead><tr><th>ID</th><th>Issue</th><th>Platform</th><th>Occurrences</th><th>Affected</th><th>Severity</th><th>Status</th></tr></thead>
            <tbody>
              {crashReports.map((r, i) => (
                <tr key={i}>
                  <td><span className="pa-badge pa-badge-blue">{r.id}</span></td>
                  <td><strong>{r.title}</strong></td>
                  <td>{r.platform}</td>
                  <td>{r.occurrences.toLocaleString()}</td>
                  <td>{r.affected_pct}</td>
                  <td><span className="pa-badge" style={{ background: (severityColor[r.severity] || '#888') + '25', color: severityColor[r.severity] || '#888' }}>{r.severity}</span></td>
                  <td><span className="pa-badge" style={{ background: (statusColor[r.status] || '#888') + '25', color: statusColor[r.status] || '#888' }}>{r.status}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="rta-chart-card" style={{ marginTop: 20 }}>
        <div className="rta-chart-head"><h6><i className="fas fa-server"></i> API Performance</h6></div>
        <div className="pa-table-wrap">
          <table className="pa-table">
            <thead><tr><th>Endpoint</th><th>P50</th><th>P95</th><th>P99</th><th>Error Rate</th><th>RPS</th><th>Status</th></tr></thead>
            <tbody>
              {apiMetrics.map((a, i) => (
                <tr key={i}>
                  <td><code style={{ fontSize: 11 }}>{a.endpoint}</code></td>
                  <td>{a.p50_ms}ms</td>
                  <td>{a.p95_ms}ms</td>
                  <td>{a.p99_ms}ms</td>
                  <td>{a.error_rate_pct}</td>
                  <td>{a.rps.toLocaleString()}</td>
                  <td><span className="pa-badge" style={{ background: (apiStatusColor[a.status] || '#888') + '25', color: apiStatusColor[a.status] || '#888' }}>{a.status}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const EngagementTab = () => (
  <div className="rta-section">
    <div className="rta-chart-card" style={{ textAlign: 'center', padding: '60px 20px' }}>
      <i className="fas fa-heart" style={{ fontSize: 48, color: '#f82581', opacity: 0.4 }}></i>
      <h6 style={{ marginTop: 16, color: '#8a8a9a' }}>Engagement metrics coming soon</h6>
      <p style={{ color: '#666', fontSize: 13 }}>This section will display detailed engagement analytics.</p>
    </div>
  </div>
);

export default RealTimeAnalytics;
